/**
 * Firebase Configuration
 *
 * Initialize Firebase services for Ostinato Labs.
 *
 * IMPORTANT: Firebase is initialized LAZILY to prevent app crashes
 * if initialization fails (e.g., network issues, invalid config).
 * All getters are cached after first successful initialization.
 *
 * @module lib/firebase
 */

import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  Functions,
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from 'firebase/functions';
import { Auth, getAuth, connectAuthEmulator } from 'firebase/auth';
import {
  Firestore,
  getFirestore,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import {
  FirebaseStorage,
  getStorage,
  connectStorageEmulator,
} from 'firebase/storage';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// ============================================================================
// Configuration
// ============================================================================

const firebaseConfig = {
  apiKey: 'AIzaSyDtGpApk_TlZBooFRzkjn1_L8hCwAOliaI',
  authDomain: 'proj-tango-01.firebaseapp.com',
  projectId: 'proj-tango-01',
  storageBucket: 'proj-tango-01.firebasestorage.app',
  messagingSenderId: '1012126632292',
  appId: '1:1012126632292:web:ac5c3ca6f8cd15c20ed271',
  measurementId: 'G-YZC4FQ5C95',
};

// ============================================================================
// Lazy Initialization (cached singletons)
// ============================================================================

let _app: FirebaseApp | null = null;
let _functions: Functions | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;
let _storage: FirebaseStorage | null = null;
let _functionsEmuConnected = false;
let _authEmuConnected = false;
let _firestoreEmuConnected = false;
let _storageEmuConnected = false;

/**
 * Get Firebase App instance (lazy initialized)
 *
 * @throws Error if Firebase initialization fails
 */
export function getFirebaseApp(): FirebaseApp {
  if (_app) return _app;

  try {
    _app = initializeApp(firebaseConfig);
    console.log('[Firebase] App initialized');
    return _app;
  } catch (error) {
    console.error('[Firebase] Failed to initialize app:', error);
    throw new Error(
      `Firebase initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Get Firebase Auth instance (lazy initialized)
 */
export function getFirebaseAuth(): Auth {
  if (_auth) return _auth;

  const app = getFirebaseApp();
  _auth = getAuth(app);
  connectEmulators();
  return _auth;
}

/**
 * Get Firebase Functions instance (lazy initialized)
 */
export function getFirebaseFunctions(): Functions {
  if (_functions) return _functions;

  const app = getFirebaseApp();
  _functions = getFunctions(app);
  connectEmulators();
  return _functions;
}

/**
 * Get Firestore instance (lazy initialized)
 */
export function getFirebaseDb(): Firestore {
  if (_db) return _db;

  const app = getFirebaseApp();
  _db = getFirestore(app);
  connectEmulators();
  return _db;
}

/**
 * Get Firebase Storage instance (lazy initialized)
 */
export function getFirebaseStorage(): FirebaseStorage {
  if (_storage) return _storage;

  const app = getFirebaseApp();
  _storage = getStorage(app);
  connectEmulators();
  return _storage;
}

/**
 * Connect each Firebase service to its emulator the first time that service
 * is initialized. Per-service flags so a later getter (e.g. getFirebaseDb)
 * still wires up its emulator even if an earlier getter (e.g. getFirebaseAuth)
 * already ran. 127.0.0.1 forces IPv4 — browsers may resolve `localhost` to
 * `::1`, which the emulator does not bind.
 */
function connectEmulators(): void {
  if (!import.meta.env.DEV) return;
  if (import.meta.env.VITE_USE_EMULATOR !== 'true') return;

  try {
    if (_functions && !_functionsEmuConnected) {
      connectFunctionsEmulator(_functions, '127.0.0.1', 5001);
      _functionsEmuConnected = true;
      console.log('[Firebase] Functions emulator connected');
    }
    if (_auth && !_authEmuConnected) {
      connectAuthEmulator(_auth, 'http://127.0.0.1:9099');
      _authEmuConnected = true;
      console.log('[Firebase] Auth emulator connected');
    }
    if (_db && !_firestoreEmuConnected) {
      connectFirestoreEmulator(_db, '127.0.0.1', 8080);
      _firestoreEmuConnected = true;
      console.log('[Firebase] Firestore emulator connected');
    }
    if (_storage && !_storageEmuConnected) {
      connectStorageEmulator(_storage, '127.0.0.1', 9199);
      _storageEmuConnected = true;
      console.log('[Firebase] Storage emulator connected');
    }
  } catch (error) {
    console.warn('[Firebase] Failed to connect to emulator:', error);
  }
}

// ============================================================================
// Backward Compatibility Exports
// ============================================================================

// These are getters that lazily initialize on first access
// This maintains backward compatibility with code that imports these directly

/** @deprecated Use getFirebaseApp() instead */
export const app = new Proxy({} as FirebaseApp, {
  get(_, prop) {
    return Reflect.get(getFirebaseApp(), prop);
  },
});

/** @deprecated Use getFirebaseFunctions() instead */
export const functions = new Proxy({} as Functions, {
  get(_, prop) {
    return Reflect.get(getFirebaseFunctions(), prop);
  },
});

/** @deprecated Use getFirebaseAuth() instead */
export const auth = new Proxy({} as Auth, {
  get(_, prop) {
    return Reflect.get(getFirebaseAuth(), prop);
  },
});

/** @deprecated Use getFirebaseDb() instead */
export const db = new Proxy({} as Firestore, {
  get(_, prop) {
    return Reflect.get(getFirebaseDb(), prop);
  },
});

/** @deprecated Use getFirebaseStorage() instead */
export const storage = new Proxy({} as FirebaseStorage, {
  get(_, prop) {
    return Reflect.get(getFirebaseStorage(), prop);
  },
});

// ============================================================================
// Messaging (already lazy)
// ============================================================================

let messagingInstance: ReturnType<typeof getMessaging> | null = null;

export async function getMessagingInstance() {
  if (messagingInstance) return messagingInstance;

  const supported = await isSupported();
  if (!supported) {
    console.warn('[Firebase] Push messaging not supported in this browser');
    return null;
  }

  messagingInstance = getMessaging(getFirebaseApp());
  return messagingInstance;
}

// ============================================================================
// Firebase Functions Callables
// ============================================================================

/**
 * Generate a song via Firebase Functions
 */
export function getGenerateSongFunction() {
  return httpsCallable<
    { prompt: string; includeLyrics?: boolean },
    { onn: unknown; quality: unknown }
  >(getFirebaseFunctions(), 'generateSong');
}

/**
 * Call the generation function
 */
export async function generateSongViaFirebase(
  prompt: string,
  options: {
    includeLyrics?: boolean;
    onProgress?: (stage: string, percent: number) => void;
  } = {}
): Promise<{ onn: unknown; quality: unknown }> {
  const { includeLyrics = true, onProgress } = options;

  onProgress?.('Connecting to server...', 5);

  try {
    const generateSongFunction = getGenerateSongFunction();
    const result = await generateSongFunction({ prompt, includeLyrics });
    onProgress?.('Done!', 100);
    return result.data;
  } catch (error) {
    throw new Error(
      `Generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

// Storage function callables
function getUploadUrlFunction() {
  return httpsCallable<
    { filename: string; contentType: string },
    { uploadUrl: string; filePath: string }
  >(getFirebaseFunctions(), 'getUploadUrl');
}

function getDownloadUrlFunction() {
  return httpsCallable<{ filePath: string }, { downloadUrl: string }>(
    getFirebaseFunctions(),
    'getDownloadUrl'
  );
}

/**
 * Get a signed URL for uploading a file
 */
export async function getUploadUrl(
  filename: string,
  contentType: string
): Promise<{ uploadUrl: string; filePath: string }> {
  const uploadUrlFunction = getUploadUrlFunction();
  const result = await uploadUrlFunction({ filename, contentType });
  return result.data;
}

/**
 * Get a signed URL for downloading a file
 */
export async function getDownloadUrl(filePath: string): Promise<string> {
  const downloadUrlFunction = getDownloadUrlFunction();
  const result = await downloadUrlFunction({ filePath });
  return result.data.downloadUrl;
}

// Messaging function callables
function getRegisterFcmTokenFunction() {
  return httpsCallable<{ token: string }, { success: boolean }>(
    getFirebaseFunctions(),
    'registerFcmToken'
  );
}

/**
 * Request notification permission and register FCM token
 */
export async function requestNotificationPermission(): Promise<string | null> {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('[Firebase] Notification permission denied');
      return null;
    }

    const messaging = await getMessagingInstance();
    if (!messaging) return null;

    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    });

    if (token) {
      // Register token with backend
      const registerFcmTokenFunction = getRegisterFcmTokenFunction();
      await registerFcmTokenFunction({ token });
      console.log('[Firebase] FCM token registered');
      return token;
    }

    return null;
  } catch (error) {
    console.error('[Firebase] Failed to get notification permission:', error);
    return null;
  }
}

/**
 * Listen for foreground messages
 */
export async function onForegroundMessage(
  callback: (payload: {
    title?: string;
    body?: string;
    data?: Record<string, string>;
  }) => void
): Promise<(() => void) | null> {
  const messaging = await getMessagingInstance();
  if (!messaging) return null;

  return onMessage(messaging, (payload) => {
    callback({
      title: payload.notification?.title,
      body: payload.notification?.body,
      data: payload.data as Record<string, string>,
    });
  });
}
